'use client'

import React from 'react'
import Image from 'next/image'
import { Box, Flex, Text, Title } from '@mantine/core'
import {
    refreshTitleVariantProps,
    refreshTextVariantProps,
    refreshTextBodyHtmlStyles,
} from '@/components/Refresh/RefreshTypography'
import { SkuGetStartedCta } from '../SkuGetStartedCta'
import { RefreshCtaButton } from '../RefreshCtaButton'
import { getSkuCtaStyles } from '@/components/SkuDetailCta.styles'
import { motion } from 'framer-motion'
import { useBreakpoint } from '@/hooks/useBreakpoints'
import { getCategoryPageData } from '@/lib/hardcoded-data/CategoryPages/getCategoryPageData'
import type { ContentAccordionData } from '@/lib/hardcoded-data/CategoryPages/types'
import { GmCrossIcon } from '@/components/Icons/GmCrossIcon'
import { textFadeInVariantsNoTransition, staggerContainer, staggerItem } from '@/lib/animations'
import { MotionBox } from '@/components/shared/AnimatedComponents'

import type { CategoryWidgetProps } from '../types'
import { parseHtml } from '@/lib/parseHtml'
import { createBulletRenderers } from '@/lib/parseHtmlBulletRenderers'

// NOTE: Two large `filter: blur(171.78px)` decorative ellipses previously rendered
// inside this widget. They were removed because they caused expensive GPU
// paint/composite work on mobile, particularly Chromium-based engines (Chrome
// Mobile, Chrome WebView, Samsung Internet) and in-app webviews (Meta, Instagram).
// See PR #2130 for the identical fix applied to the homepage TopBar. The blue-tinted
// glow was barely perceptible at 0.15/0.25 opacity behind the dark gradient and the
// runtime cost far outweighed the visual benefit. Approximated below with static
// radial-gradient layers in the container background — cheap to paint, no kernel
// pass on the GPU.

export default function WidgetContentAccordion({
    categoryName,
    data: dataProp,
    primaryService,
    primarySku,
    pageLocation,
    additionalInformation,
}: CategoryWidgetProps) {
    const { isSm, isMd, isBelow990px } = useBreakpoint()
    const data = (dataProp ?? getCategoryPageData(categoryName, 'contentAccordion')) as ContentAccordionData | null

    if (!data || !data.images?.length) return null

    const { title, subtitle, accordionItems, images } = data
    const [clinicianImage, phoneImage] = images
    const showAccordionGrid = accordionItems.length > 0
    const ctaLabel = data.primaryCta?.label ?? 'Book a visit'
    const sectionCtaHref = data.primaryCta?.href
    const primaryBlueCtaStyle = getSkuCtaStyles('primary-blue')

    return (
        <Box
            id="contentAccordion"
            component={motion.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px 0px' }}
            variants={staggerContainer}
            maw={1440}
            mx="auto"
            w="100%"
            px={{ base: 24, sm: 60 }}
            py={{ base: 48, sm: 60 }}
            style={{
                position: 'relative',
                background:
                    'radial-gradient(ellipse at 0% 0%, rgba(121, 103, 255, 0.15) 0%, transparent 45%),' +
                    'radial-gradient(ellipse at 100% 35%, rgba(169, 181, 255, 0.18) 0%, transparent 55%),' +
                    'linear-gradient(99deg, #201D2F 37.95%, #000 95.91%)',
                borderRadius: isSm ? 0 : 24,
                overflow: 'hidden',
                ...(isSm && {
                    width: '100vw',
                    marginLeft: 'calc(50% - 50vw)',
                    marginRight: 'calc(50% - 50vw)',
                }),
            }}
        >
            {/* Top section: CategoryHeader-style layout */}
            <Flex
                direction={{ base: 'column', sm: 'column', md: 'row' }}
                gap={{ base: 32, sm: 60 }}
                pb={showAccordionGrid ? 60 : 0}
                align="stretch"
                style={{ minWidth: 0 }}
            >
                {/* Left: icon, title, description, CTA */}
                <Flex flex={1} direction="column" justify="center" gap={24} style={{ minWidth: 0 }}>
                    <MotionBox variants={textFadeInVariantsNoTransition}>
                        <GmCrossIcon width={60} height={59} fill="#F5F1ED" />
                    </MotionBox>
                    <MotionBox variants={textFadeInVariantsNoTransition}>
                        <Flex direction="column" justify="center" gap={{ base: 16, sm: 24 }} style={{ minWidth: 0 }}>
                            <Title {...refreshTitleVariantProps.hero} mt={36} mb={16} maw={isBelow990px ? undefined : 533}>
                                {title}
                            </Title>

                            <Text
                                {...refreshTextVariantProps.bodyLead}
                                component="div"
                                c="white"
                                maw={isBelow990px ? undefined : 530}
                                styles={refreshTextBodyHtmlStyles}
                            >
                                {parseHtml(
                                    subtitle,
                                    createBulletRenderers({ c: 'white', fz: 14, lh: '130%' }),
                                )}
                            </Text>

                            {sectionCtaHref ? (
                                <RefreshCtaButton
                                    href={sectionCtaHref}
                                    bg={primaryBlueCtaStyle.bg}
                                    c={primaryBlueCtaStyle.c}
                                    style={{ width: 'fit-content' }}
                                >
                                    {ctaLabel}
                                </RefreshCtaButton>
                            ) : (
                                <SkuGetStartedCta
                                    sku={primarySku}
                                    service={primaryService}
                                    theme="primary-blue"
                                    label={ctaLabel}
                                    fallbackHref={
                                        (
                                            getCategoryPageData(categoryName, 'categoryHeader') as {
                                                primaryCta?: { href?: string }
                                            }
                                        )?.primaryCta?.href ?? '/visit'
                                    }
                                    fallbackLabel={ctaLabel}
                                    pageLocation={pageLocation}
                                    additionalInformation={additionalInformation}
                                    style={{ width: 'fit-content' }}
                                />
                            )}
                        </Flex>
                    </MotionBox>
                </Flex>

                {/* Right: two images in relative wrapper (phone top-right, clinician bottom-left) */}
                <Box
                    component={motion.div}
                    variants={textFadeInVariantsNoTransition}
                    style={{
                        position: 'relative',
                        width: isSm ? 350 : '100%',
                        height: isSm ? 305 : undefined,
                        minHeight: isSm ? undefined : 400,
                        flex: isSm ? 'none' : '1 1 0',
                        alignSelf: isSm ? 'center' : 'stretch',
                        minWidth: 0,
                    }}
                >
                    {/* Phone image: top-right */}
                    <Box
                        style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: isSm ? 280 : '80.04%',
                            height: isSm ? 245 : '85%',
                            borderRadius: 20,
                            overflow: 'hidden',
                            zIndex: 1,
                        }}
                    >
                        <Image
                            src={phoneImage}
                            alt="Your care plan"
                            fill
                            style={{ objectFit: 'cover' }}
                            sizes={isSm ? '280px' : '(max-width: 768px) 280px, 485px'}
                        />
                    </Box>
                    {/* Clinician image: bottom-left */}
                    <Box
                        style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: isSm ? 132 : '37.79%',
                            height: isSm ? 184 : '50%',
                            maxHeight: isSm ? undefined : 320,
                            borderRadius: 16,
                            overflow: 'hidden',
                            zIndex: 2,
                        }}
                    >
                        <Image
                            src={clinicianImage}
                            alt="Clinician"
                            fill
                            style={{ objectFit: 'cover' }}
                            sizes={isSm ? '132px' : '(max-width: 768px) 132px, 229px'}
                        />
                    </Box>
                </Box>
            </Flex>

            {showAccordionGrid ? (
                <MotionBox
                    variants={staggerContainer}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-50px 0px' }}
                    style={{
                        marginTop: isSm ? 24 : 48,
                        display: 'grid',
                        gridTemplateColumns: isSm ? '1fr' : isMd ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                        gap: isSm ? 32 : 60,
                        width: '100%',
                    }}
                >
                    {accordionItems.map((item, index) => (
                        <MotionBox key={index} variants={staggerItem}>
                            <Box style={{ borderTop: '1px solid rgba(255, 255, 255, 0.20)', paddingTop: 24 }}>
                                <Text {...refreshTextVariantProps.statValue} component="div" c="white" lts={-0.48} mb={16}>
                                    {index + 1}
                                </Text>
                                <Text {...refreshTextVariantProps.accordionItemTitle}>{item.title}</Text>
                                <Text
                                    {...refreshTextVariantProps.body}
                                    component="div"
                                    size="sm"
                                    lh="150%"
                                    c="white"
                                    styles={refreshTextBodyHtmlStyles}
                                >
                                    {parseHtml(item.description, createBulletRenderers({ c: 'white' }))}
                                </Text>
                            </Box>
                        </MotionBox>
                    ))}
                </MotionBox>
            ) : null}
        </Box>
    )
}
